import React, { useRef, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import Navbar from "@/components/Navbar";
import TopUrgencyBar from "@/components/TopUrgencyBar";
import Footer from "@/components/Footer";
import SignaturePad from "@/components/SignaturePad";
import { CheckCircle2, ArrowRight, Shield, FileSignature, Loader2, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TICKET_TYPES = [
  "Day Pass",
  "Weekend Pass",
  "Woman's Bunk Spot",
  "Queen Room",
  "King Master Suite",
];

// Waiver copy — kept in sync with backend PDF generator (waiver_pdf.py).
const WAIVER_INTRO = (
  <>
    In consideration of being permitted to participate in the SSU Workshops event (the &ldquo;Event&rdquo;) organized by{" "}
    <strong>SSA MEDIA LLC</strong> (&ldquo;SSA Media&rdquo; or &ldquo;Releasee&rdquo;), I, the undersigned
    participant, hereby acknowledge, agree, and voluntarily execute this Liability Waiver and Release of Claims
    (this &ldquo;Release&rdquo;) in favor of SSA MEDIA LLC, its owners, officers, directors, members, employees, agents,
    contractors, instructors, presenters, sponsors, affiliates, and representatives (collectively, the
    &ldquo;Released Parties&rdquo;).
  </>
);

const WAIVER_SECTIONS = [
  {
    h: "1. ASSUMPTION OF RISK.",
    p: (
      <>
        I understand that the Event includes in-water instructional sessions, group activities, use of a private
        residential estate and its amenities (including swimming pool, fitness and parkour equipment, tennis court,
        and grill/fire features), and other physical activities that carry inherent risks. Such risks include, but
        are not limited to: drowning or near-drowning, slips, trips, and falls, cuts, bruises, muscle strains,
        sprains, fractures, heat-related illness, burns, allergic reactions, equipment failure, contact with other
        participants, contact with pool decks or fixtures, communicable diseases, and travel to and from the venue.
        I understand that these risks cannot be eliminated regardless of the care taken to avoid injuries.{" "}
        <strong>I VOLUNTARILY ASSUME ALL RISKS</strong>, both known and unknown, associated with my participation.
      </>
    ),
  },
  {
    h: "2. RELEASE AND WAIVER.",
    p: (
      <>
        To the fullest extent permitted by law, I hereby <strong>RELEASE, WAIVE, DISCHARGE, and COVENANT NOT TO SUE</strong>{" "}
        the Released Parties from any and all liability, claims, demands, actions, and causes of action whatsoever,
        whether arising in negligence or otherwise, for any loss, damage, injury, illness, or death sustained by me
        or damage to my property while participating in, traveling to or from, or otherwise being present at the
        Event, <strong>INCLUDING SUCH CLAIMS ARISING FROM THE NEGLIGENCE OF THE RELEASED PARTIES</strong>.
      </>
    ),
  },
  {
    h: "3. INDEMNIFICATION.",
    p: (
      <>
        I agree to <strong>INDEMNIFY, DEFEND, AND HOLD HARMLESS</strong> the Released Parties from any losses,
        liabilities, damages, or costs (including reasonable attorneys&rsquo; fees) they may incur due to my
        participation in the Event or from any breach of this Release by me.
      </>
    ),
  },
  {
    h: "4. FITNESS AND MEDICAL.",
    p: (
      <>
        I represent that I am in good physical health and have no medical condition that would prevent my safe
        participation. I have consulted a physician if I have any doubt about my fitness. I agree to disclose any
        medical conditions or allergies relevant to my safety at the Event and to follow all instructions from Event
        staff.
      </>
    ),
  },
  {
    h: "5. PHOTO/VIDEO RELEASE.",
    p: (
      <>
        I grant SSA MEDIA LLC and the Released Parties the irrevocable right to photograph, record, and use my
        likeness, name, voice, and statements in any medium for promotional, marketing, educational, and commercial
        purposes related to SSU Workshops, without further compensation or approval.
      </>
    ),
  },
  {
    h: "6. RULES AND CONDUCT.",
    p: (
      <>
        I agree to abide by all Event rules, follow the reasonable instructions of instructors and hosts, respect
        the venue and other participants, and refrain from conduct that could cause harm to persons or property.
        SSA Media reserves the right to remove any participant whose behavior is unsafe or disruptive, without refund.
      </>
    ),
  },
  {
    h: "7. NO REFUND FOR NON-PARTICIPATION.",
    p: (
      <>
        I understand that refunds are governed by the ticket purchase terms and that my decision not to participate
        in any portion of the Event does not entitle me to a refund unless expressly stated.
      </>
    ),
  },
  {
    h: "8. GOVERNING LAW; SEVERABILITY.",
    p: (
      <>
        This Release shall be governed by the laws of the State of Florida, without regard to its conflict-of-laws
        principles. If any provision is held unenforceable, the remaining provisions shall remain in full force and effect.
      </>
    ),
  },
  {
    h: "9. ELECTRONIC SIGNATURE.",
    p: (
      <>
        I agree that my electronic signature below constitutes a legally binding signature under the Electronic
        Signatures in Global and National Commerce Act (E-SIGN) and applicable state laws.
      </>
    ),
  },
];

const ACKS = [
  { key: "ack_read", label: "I have read and fully understand this Liability Waiver and Release of Claims." },
  { key: "ack_release", label: "I voluntarily release, waive, and discharge SSA MEDIA LLC and the Released Parties from any and all claims described above." },
  { key: "ack_physical", label: "I confirm I am physically able to participate in the workshop activities, including in-water sessions, and I have disclosed any relevant medical conditions or allergies." },
  { key: "ack_media", label: "I consent to SSA MEDIA LLC's use of my photograph, video, name, and likeness for marketing and promotional purposes." },
];

export default function Waiver() {
  const sigRef = useRef(null);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    date_of_birth: "",
    ticket_type: "",
  });
  const [acks, setAcks] = useState({ ack_read: false, ack_release: false, ack_physical: false, ack_media: false });
  const [hasSig, setHasSig] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState("");
  const [pdfFilename, setPdfFilename] = useState("SSU-Waiver.pdf");

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleAck = (k) => setAcks((a) => ({ ...a, [k]: !a[k] }));

  const allAcksChecked = ACKS.every((a) => acks[a.key]);

  // Turn a base64 PDF returned by the API into a downloadable blob URL,
  // then auto-trigger the download once so the signer instantly gets a copy.
  const preparePdfDownload = (pdfBase64, filename) => {
    if (!pdfBase64) return;
    try {
      const binary = atob(pdfBase64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const blob = new Blob([bytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPdfBlobUrl(url);
      setPdfFilename(filename || "SSU-Waiver.pdf");
      // Auto-trigger a download so it feels seamless. The button below lets them re-download.
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "SSU-Waiver.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      // If decoding fails just leave the button hidden; the record is still saved server-side.
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    // Required text fields
    const required = ["first_name", "last_name", "email", "phone", "date_of_birth", "ticket_type"];
    for (const k of required) {
      if (!String(form[k] || "").trim()) {
        toast.error("Please fill in every required field before signing.");
        return;
      }
    }
    if (!allAcksChecked) {
      toast.error("Please check all four acknowledgments before signing.");
      return;
    }
    const signature_data_url = sigRef.current?.getDataURL() || "";
    if (!signature_data_url) {
      toast.error("Please sign in the signature box.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = { ...form, ...acks, signature_data_url };
      const res = await axios.post(`${API}/waiver`, payload);
      if (res.data?.success) {
        preparePdfDownload(res.data.pdf_base64, res.data.pdf_filename);
        setConfirmed(true);
        toast.success("Waiver signed. Your PDF is downloading now.");
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        toast.error("Something went wrong — please try again.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Submission failed — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="waiver-page">
      <TopUrgencyBar />
      <Navbar onSaveSpot={() => (window.location.href = "/#tickets")} />

      {/* Hero */}
      <header className="relative bg-[#0A1128] text-white overflow-hidden pt-[124px] md:pt-[128px]">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A1128] via-[#0F1B3D] to-[#0A1128]" />
          <div className="absolute top-10 left-10 w-80 h-80 bg-[#FACC15]/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-10 right-0 w-96 h-96 bg-[#0066FF]/15 rounded-full blur-3xl pointer-events-none" />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 lg:px-10 py-14 md:py-20 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#FACC15]/10 border border-[#FACC15]/30 text-[#FACC15] rounded-full mb-5 text-[10px] md:text-xs font-black uppercase tracking-[0.25em]">
            <Shield size={12} /> Required Before Arrival
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-4 leading-[0.95]">
            Liability <span className="text-[#FACC15]">Waiver</span>
          </h1>
          <p className="text-lg text-white/75 max-w-2xl mx-auto">
            One quick form to sign before the workshop. Fill it out, check the boxes, sign with your finger or mouse — a
            signed PDF will download right after you submit. Keep it for your records.
          </p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-12 md:py-16">
        {confirmed ? (
          <ConfirmationScreen
            firstName={form.first_name}
            pdfBlobUrl={pdfBlobUrl}
            pdfFilename={pdfFilename}
          />
        ) : (
          <form onSubmit={submit} className="space-y-8" data-testid="waiver-form">
            {/* Participant details */}
            <section className="bg-white border border-[#E5E7EB] rounded-2xl p-6 md:p-8 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-black tracking-[0.25em] uppercase text-[#0066FF]">01</span>
                <span className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Your Details</span>
              </div>
              <h2 className="font-display-tight text-2xl md:text-3xl text-[#0A1128] mb-6">Participant Information</h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="First name" required>
                  <input
                    type="text"
                    value={form.first_name}
                    onChange={(e) => setField("first_name", e.target.value)}
                    data-testid="waiver-first-name"
                    className={inputCls}
                    autoComplete="given-name"
                  />
                </Field>
                <Field label="Last name" required>
                  <input
                    type="text"
                    value={form.last_name}
                    onChange={(e) => setField("last_name", e.target.value)}
                    data-testid="waiver-last-name"
                    className={inputCls}
                    autoComplete="family-name"
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setField("email", e.target.value)}
                    data-testid="waiver-email"
                    className={inputCls}
                    autoComplete="email"
                  />
                </Field>
                <Field label="Phone" required>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setField("phone", e.target.value)}
                    data-testid="waiver-phone"
                    className={inputCls}
                    autoComplete="tel"
                  />
                </Field>
                <Field label="Date of birth" required>
                  <input
                    type="date"
                    value={form.date_of_birth}
                    onChange={(e) => setField("date_of_birth", e.target.value)}
                    data-testid="waiver-dob"
                    className={inputCls}
                    autoComplete="bday"
                  />
                </Field>
                <Field label="Ticket type" required>
                  <select
                    value={form.ticket_type}
                    onChange={(e) => setField("ticket_type", e.target.value)}
                    data-testid="waiver-ticket-type"
                    className={inputCls}
                  >
                    <option value="">Select your ticket…</option>
                    {TICKET_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </Field>
              </div>
            </section>

            {/* Waiver text */}
            <section className="bg-white border border-[#E5E7EB] rounded-2xl p-6 md:p-8 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-black tracking-[0.25em] uppercase text-[#0066FF]">02</span>
                <span className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Please Read</span>
              </div>
              <h2 className="font-display-tight text-2xl md:text-3xl text-[#0A1128] mb-4">
                Liability Waiver and Release of Claims
              </h2>

              <div
                data-testid="waiver-text"
                className="text-sm leading-relaxed text-[#374151] space-y-4 max-h-[420px] overflow-y-auto pr-2 border border-[#E5E7EB] rounded-xl p-4 md:p-5 bg-[#FAFAF7]"
              >
                <p>{WAIVER_INTRO}</p>
                {WAIVER_SECTIONS.map((s) => (
                  <div key={s.h}>
                    <div className="font-bold text-[#0A1128] mb-1">{s.h}</div>
                    <p>{s.p}</p>
                  </div>
                ))}
                <p className="italic text-[#4B5563] border-t border-[#E5E7EB] pt-3">
                  By signing below, I certify that I have read this entire Release, understand it, agree to its terms
                  voluntarily and without coercion, and that this Release binds me, my heirs, personal representatives,
                  and assigns.
                </p>
              </div>
            </section>

            {/* Acknowledgments */}
            <section className="bg-white border border-[#E5E7EB] rounded-2xl p-6 md:p-8 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-black tracking-[0.25em] uppercase text-[#0066FF]">03</span>
                <span className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Confirm</span>
              </div>
              <h2 className="font-display-tight text-2xl md:text-3xl text-[#0A1128] mb-2">Acknowledgments</h2>
              <p className="text-sm text-[#4B5563] mb-5">Please check all four boxes.</p>

              <div className="space-y-3">
                {ACKS.map((a) => {
                  const checked = acks[a.key];
                  return (
                    <label
                      key={a.key}
                      data-testid={`ack-${a.key}`}
                      className={`flex items-start gap-3 border-2 rounded-xl p-4 cursor-pointer transition-all ${
                        checked ? "border-[#22C55E] bg-[#F0FDF4]" : "border-[#E5E7EB] bg-white hover:border-[#0066FF]/50"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleAck(a.key)}
                        data-testid={`ack-${a.key}-input`}
                        className="mt-1 h-5 w-5 accent-[#22C55E] shrink-0"
                      />
                      <span className="text-sm text-[#0A1128] leading-relaxed">{a.label}</span>
                    </label>
                  );
                })}
              </div>
            </section>

            {/* Signature */}
            <section className="bg-white border border-[#E5E7EB] rounded-2xl p-6 md:p-8 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-black tracking-[0.25em] uppercase text-[#0066FF]">04</span>
                <span className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Sign</span>
              </div>
              <h2 className="font-display-tight text-2xl md:text-3xl text-[#0A1128] mb-4 flex items-center gap-2">
                <FileSignature size={22} className="text-[#0066FF]" /> Your Signature
              </h2>
              <SignaturePad ref={sigRef} onChange={setHasSig} />
            </section>

            {/* Submit */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <button
                type="submit"
                disabled={submitting}
                data-testid="waiver-submit"
                className="btn-shine inline-flex items-center gap-2 bg-[#FACC15] hover:bg-[#EAB308] text-[#0A1128] font-black uppercase tracking-wider text-sm px-7 py-3.5 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Submitting…
                  </>
                ) : (
                  <>
                    Sign &amp; Submit Waiver <ArrowRight size={16} />
                  </>
                )}
              </button>
              <span className="text-xs text-[#6B7280]">
                Your signed PDF will download instantly. A copy is also stored securely with SSA MEDIA LLC.
              </span>
            </div>
          </form>
        )}
      </main>

      <Footer />
    </div>
  );
}

const inputCls =
  "w-full border-2 border-[#E5E7EB] focus:border-[#0066FF] focus:outline-none rounded-lg px-3 py-2.5 text-[#0A1128] bg-white transition-colors";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">
        {label}{required && <span className="text-[#EF4444]"> *</span>}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function ConfirmationScreen({ firstName, pdfBlobUrl, pdfFilename }) {
  return (
    <div
      data-testid="waiver-confirmation"
      className="bg-white border-2 border-[#22C55E]/40 rounded-2xl p-8 md:p-12 text-center shadow-sm"
    >
      <div className="w-16 h-16 mx-auto rounded-full bg-[#22C55E] text-white flex items-center justify-center mb-5">
        <CheckCircle2 size={32} />
      </div>
      <div className="text-[10px] font-black tracking-[0.25em] uppercase text-[#166534] mb-2">Signed &amp; On File</div>
      <h2 className="font-display text-3xl md:text-5xl text-[#0A1128] leading-[0.95] mb-4">
        Waiver Received{firstName ? `, ${firstName}` : ""}.
      </h2>
      <p className="text-[#374151] text-lg max-w-xl mx-auto">
        Your signed waiver is downloading now. Keep the PDF for your records — nothing else needed on your end.
      </p>
      <p className="text-[#6B7280] text-sm mt-3 max-w-xl mx-auto">
        If your browser blocked the download, tap the button below to save it manually.
      </p>

      <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
        {pdfBlobUrl && (
          <a
            href={pdfBlobUrl}
            download={pdfFilename}
            data-testid="waiver-download"
            className="inline-flex items-center gap-2 bg-[#FACC15] hover:bg-[#EAB308] text-[#0A1128] font-black uppercase tracking-wider text-sm px-6 py-3 rounded-lg transition-colors"
          >
            <Download size={16} /> Download Signed Waiver
          </a>
        )}
        <a
          href="/"
          data-testid="waiver-back-home"
          className="inline-flex items-center gap-2 border-2 border-[#0A1128] text-[#0A1128] hover:bg-[#0A1128] hover:text-white font-black uppercase tracking-wider text-sm px-6 py-3 rounded-lg transition-colors"
        >
          Back to Home <ArrowRight size={16} />
        </a>
      </div>
    </div>
  );
}
